/**
 * Utilitário para processar diagramas Mermaid em documentação
 *
 * Este arquivo contém funções dedicadas a processar e renderizar
 * corretamente diagramas Mermaid em conteúdo Markdown.
 */

// Configuração principal do Mermaid para todos os tipos de diagramas
function configureMermaid() {
  const mermaid = getMermaid();
  if (!mermaid) return;

  try {
    mermaid.initialize({
      startOnLoad: false,
      theme: document.documentElement.classList.contains('dark') ? 'dark' : 'default',
      securityLevel: 'loose',
      fontFamily: 'inherit',
      fontSize: 14,
      // Configuração específica para flowchart e graph TD
      flowchart: {
        htmlLabels: true,
        curve: 'basis', // Mudado para basis para melhor visualização
        useMaxWidth: false,
        defaultRenderer: 'dagre-wrapper' // Renderer mais robusto
      },
      // Configuração específica para sequenceDiagram
      sequence: {
        diagramMarginX: 50,
        diagramMarginY: 10,
        actorMargin: 80, // Aumentado para mais espaço
        width: 150,
        height: 65,
        boxMargin: 10,
        boxTextMargin: 5,
        noteMargin: 10,
        messageMargin: 40, // Aumentado para mensagens mais claras
        mirrorActors: false,
        useMaxWidth: false,
        rightAngles: false, // Linhas em ângulo reto (false para curvas)
        wrap: true // Permite quebra de texto
      },
      er: {
        diagramPadding: 20,
        layoutDirection: 'TB',
        minEntityWidth: 100,
        minEntityHeight: 75,
        entityPadding: 15,
        useMaxWidth: false
      },
      gantt: {
        titleTopMargin: 25,
        barHeight: 20,
        barGap: 4,
        topPadding: 50,
        leftPadding: 75,
        gridLineStartPadding: 35,
        fontSize: 11,
        useMaxWidth: false
      },
      pie: {
        useMaxWidth: false
      },
      // Configurações adicionais para depuração
      logLevel: 'warn', // Aumentado para ver mais mensagens de erro
      deterministicIds: true,
      maxTextSize: 500000, // Aumentado para suportar diagramas maiores
    });

  } catch (e) {
  }
}

// Processa diagramas em blocos de código
function processMermaidCodeBlocks() {
  const mermaid = getMermaid();
  if (!mermaid) return;

  try {
    // Procura por blocos de código com classe 'language-mermaid'
    const mermaidCodeBlocks = document.querySelectorAll('pre code.language-mermaid');
    mermaidCodeBlocks.forEach((codeBlock, index) => {
      try {
        const content = codeBlock.textContent?.trim() || '';
        if (content) {
          // Cria um novo div para o diagrama Mermaid
          const mermaidDiv = document.createElement('div');
          mermaidDiv.className = 'mermaid';
          mermaidDiv.id = `mermaid-code-${index}`;
          mermaidDiv.textContent = content;

          // Substitui o bloco pre pelo div do mermaid
          const preElement = codeBlock.closest('pre');
          if (preElement && preElement.parentNode) {
            preElement.parentNode.replaceChild(mermaidDiv, preElement);
          }
        }
      } catch (err) {
      }
    });

  } catch (e) {
  }
}

// Processa blocos que podem conter sintaxe Mermaid mas não foram identificados como tal
function processUnrecognizedMermaidBlocks() {
  const mermaid = getMermaid();
  if (!mermaid) return;

  try {
    // Busca todos os blocos de código
    const codeBlocks = document.querySelectorAll('pre code:not(.language-mermaid)');
    let convertedBlocks = 0;

    codeBlocks.forEach((block, index) => {
      const content = block.textContent?.trim() || '';

      // Verifica se o conteúdo parece ser um diagrama Mermaid
      // Adicionamos variações específicas dos diagramas problemáticos
      const isMermaid =
        // Graph/Flowchart - tipos problemáticos
        content.startsWith('graph ') ||
        content.startsWith('graph TD') ||
        content.startsWith('graph LR') ||
        content.startsWith('flowchart ') ||
        content.startsWith('flowchart TD') ||
        content.startsWith('flowchart LR') ||
        // Sequence - tipo problemático
        content.startsWith('sequenceDiagram') ||
        // Outros tipos
        content.startsWith('classDiagram') ||
        content.startsWith('erDiagram') ||
        content.startsWith('gantt') ||
        content.startsWith('pie') ||
        content.startsWith('stateDiagram');

      if (isMermaid) {
        // Cria um novo div para o diagrama Mermaid
        const mermaidDiv = document.createElement('div');
        mermaidDiv.className = 'mermaid';
        mermaidDiv.id = `mermaid-unrecognized-${index}`;

        // Verifica se há problemas conhecidos e corrige se possível
        let processedContent = content;

        // Corrige graph TD sem espaço
        if (content.startsWith('graphTD')) {
          processedContent = 'graph TD' + content.substring(7);
        }

        // Corrige flowchart TD sem espaço
        if (content.startsWith('flowchartTD')) {
          processedContent = 'flowchart TD' + content.substring(11);
        }

        // Garante espaço após a definição de nós em flowcharts
        processedContent = processedContent.replace(/(\[[^\]]+\])([^\s])/g, '$1 $2');

        mermaidDiv.textContent = processedContent;

        // Substitui o bloco pre pelo div do mermaid
        const preElement = block.closest('pre');
        if (preElement && preElement.parentNode) {
          preElement.parentNode.replaceChild(mermaidDiv, preElement);
          convertedBlocks++;
        }
      }
    });
  } catch (e) {
  }
}

// Renderiza todos os diagramas Mermaid na página
function renderMermaidDiagrams() {
  const mermaid = getMermaid();
  if (!mermaid) return;

  try {
    mermaid.run({
      querySelector: '.mermaid',
      suppressErrors: false
    }).then(() => {
      // Todos os diagramas foram renderizados com sucesso
    }).catch((error) => {

      // Se falhar, tenta renderizar cada diagrama individualmente
      renderMermaidDiagramsIndividually();
    });
  } catch (e) {
    renderMermaidDiagramsIndividually();
  }
}

// Tenta renderizar cada diagrama individualmente para isolar falhas
function renderMermaidDiagramsIndividually() {
  const mermaid = getMermaid();
  if (!mermaid) return;
  const mermaidDivs = document.querySelectorAll('.mermaid');

  mermaidDivs.forEach((element, index) => {
    try {
      const id = `mermaid-diagram-${index}`;
      const content = element.textContent || '';

      // Verifica se já foi renderizado (tem um SVG como filho)
      if (element.querySelector('svg')) {
        return;
      }

      mermaid.render(id, content).then((result) => {
        element.innerHTML = result.svg;
      }).catch((err) => {
        element.innerHTML = `
          <div class="p-4 border border-red-500 rounded">
            <p class="text-red-500 font-bold">Erro no diagrama Mermaid:</p>
            <pre class="mt-2 text-xs overflow-auto">${content}</pre>
          </div>
        `;
      });
    } catch (individualError) {
    }
  });
}

// Verifica especificamente os tipos de diagramas problemáticos
function checkProblematicDiagrams() {
  const mermaid = getMermaid();
  if (!mermaid) return;

  try {
    const mermaidDivs = document.querySelectorAll('.mermaid');
    let countProblematic = 0;

    mermaidDivs.forEach((div, index) => {
      const content = div.textContent || '';

      // Verifica se é um dos tipos problemáticos mencionados
      const isProblematic =
        content.startsWith('graph TD') ||
        content.startsWith('flowchart TD') ||
        content.startsWith('sequenceDiagram');

      if (isProblematic) {
        countProblematic++;

        // Aplicar correções específicas por tipo
        let fixedContent = content;

        // Para flowchart TD, certificamos que as setas estão corretas
        if (content.startsWith('flowchart TD') || content.startsWith('graph TD')) {
          // Corrige setas mal formadas
          fixedContent = fixedContent.replace(/-->/g, ' --> ');
          fixedContent = fixedContent.replace(/--([^>])/g, '-- $1');

          // Certifica-se que nós têm espaço depois
          fixedContent = fixedContent.replace(/(\[[^\]]+\])([^\s])/g, '$1 $2');
        }

        // Para sequenceDiagram, certifica participantes e mensagens
        if (content.startsWith('sequenceDiagram')) {
          // Corrige setas de mensagem
          fixedContent = fixedContent.replace(/->/g, ' -> ');
          fixedContent = fixedContent.replace(/->>/g, ' ->> ');
        }

        // Se houve correção, atualiza o conteúdo
        if (fixedContent !== content) {
          div.textContent = fixedContent;
        }

        // Adiciona uma classe para estilo diferenciado
        div.classList.add('problematic-diagram');
      }
    });

  } catch (e) {
  }
}

// Função segura para acessar o mermaid global
function getMermaid() {
  if (typeof window === 'undefined' || !window.mermaid) {
    return null;
  }
  return window.mermaid;
}

// Processar todos os diagramas Mermaid na página
function processMermaidDiagrams() {
  // Se Mermaid não estiver disponível, tenta novamente após um atraso
  const mermaid = getMermaid();
  if (!mermaid) {
    setTimeout(processMermaidDiagrams, 100);
    return;
  }
  try {
    // 1. Configura o Mermaid
    configureMermaid();

    // 2. Processa blocos de código Mermaid
    processMermaidCodeBlocks();

    // 3. Processa blocos não reconhecidos
    processUnrecognizedMermaidBlocks();

    // 4. Verifica especificamente diagramas problemáticos
    checkProblematicDiagrams();

    // 5. Renderiza todos os diagramas
    // Pequeno atraso para garantir que tudo está pronto
    setTimeout(renderMermaidDiagrams, 100);
  } catch (e) {
  }
}

// Exporta as funções para uso em outros arquivos
export {
  processMermaidDiagrams,
  configureMermaid,
  renderMermaidDiagrams,
  checkProblematicDiagrams
};
